/**
 * HelpBob Fixer Client
 *
 * Lightweight script that loads on customer websites to apply
 * accessibility patches from the HelpBob fixer server.
 *
 * KEY DIFFERENCES FROM OVERLAYS:
 * - No blanket "fix everything" approach
 * - Each patch is individually audited and toggleable
 * - Never overwrites existing ARIA attributes
 * - Fully reversible - every patch has a rollback
 * - Transparent: patches are visible via data-helpbob-* attributes
 * - No widget/UI injected (admin panel is separate)
 *
 * SPA SUPPORT:
 * - MutationObserver watches for dynamically added DOM nodes
 * - Route change detection re-fetches patches for new URLs
 * - Shadow DOM traversal for Web Components
 */

import type { FixerResponse, PublicPatch } from '@helpbob/shared';
// Direkt das Modul, nicht der Paketeinstieg: der zieht als CommonJS den ganzen
// Rest von @helpbob/shared ins Kunden-Bundle (Budget, Gate 3).
import { normalizePageUrl, reportPageUrl } from '@helpbob/shared/dist/page-url';
import { PatchApplier } from './patch-applier';
import { MutationWatcher, type MutationWatcherStats } from './mutation-watcher';
import { RouteDetector } from './route-detector';
import { PatchAnalytics } from './analytics';
import { mountReviewPanel, type ReviewChange, type ReviewChangeState } from './review-panel';
import { resolveWidgetLocale, errorStrings, panelStrings, type WidgetLocale } from './widget-i18n';
import { describeAction } from './describe-action';
import { querySelectorAllDeep } from './shadow-query';

export interface HelpBobConfig {
  /** Fixer server URL */
  serverUrl: string;
  /** Site API key */
  apiKey: string;
  /** Whether to apply patches automatically (default: true) */
  autoApply?: boolean;
  /** Cache TTL in seconds (default: 300 = 5 min) */
  cacheTtl?: number;
  /** Enable dynamic DOM watching via MutationObserver (default: true) */
  watchDom?: boolean;
  /** Enable SPA route change detection (default: true) */
  watchRoutes?: boolean;
  /** Enable client-side analytics reporting (default: true) */
  analytics?: boolean;
  /** UI language for the in-page review surfaces ('en' | 'de'). Defaults to the
      page's <html lang>, then English. Does not affect the silent patcher. */
  locale?: 'en' | 'de';
}

/**
 * Patch cache: one localStorage entry PER PAGE (scanner#352). The key carries
 * the same string we send as `url` — the page address without fragment and
 * tracking parameters (normalizePageUrl, scanner#355), so neither `#main` nor
 * `#access_token=…` nor `?utm_source=…` ever lands in the customer's storage.
 * Under the old single fixed key every page loaded within cacheTtl got the
 * previous page's patches (an html-selector patch such as F110's
 * scroll-padding applies anywhere) and never its own. The prefix also keeps
 * old bundles (fixed key, no 'compound' cap) and this one from ever reading
 * each other's entries.
 */
const CACHE_PREFIX = 'helpbob_patches:v2:';
/** The pre-#352 fixed key — removed on write, never read. */
const LEGACY_CACHE_KEY = 'helpbob_patches';
/** Most pages cached at once — we share the customer's storage quota. */
const CACHE_MAX_ENTRIES = 20;

/**
 * Patch action types this bundle understands beyond the original set, sent as
 * `caps` on GET /patches (scanner#61). The snippet carries no version, and a
 * bundle cached before a type existed skips it in runAction while still
 * counting the element as applied — so the server hands such a type only to a
 * client that declares it (fixer-server/src/routes/patches.ts).
 */
//
// Paket D: `renameId` (the F77 fix) and `target` — not an action type but a
// patch field: a bundle that does not know it would apply a targeted patch to
// EVERY match of its selector, i.e. exactly the wrong-element defect the note
// exists to prevent.
// Paket FX (27.10.2026): `motionPause` — this bundle builds and wires the F16
// pause control itself (patch-applier.ts insertMotionPause); an older one
// would refuse or, before 13.10., insert a button without behaviour.
const CLIENT_CAPS = 'compound,renameId,target,motionPause';

/**
 * How often one load re-fetches because the page moved while it waited
 * (scanner#355) — a start-up redirect chain `/` → `/de/` → `/de/start` is two
 * hops. A page that is still moving after that gets nothing from this load;
 * the route detector loads again on its next change.
 */
const MAX_LOAD_ATTEMPTS = 3;

/**
 * The page we are on, as the patch lookup names it (scanner#355): the href
 * without fragment and tracking parameters — the same function the server
 * applies to `url` and to the scanned page address.
 */
function currentPageUrl(): string {
  return normalizePageUrl(window.location.href);
}

/**
 * Review-token handoff (scanner#197). The dashboard's deep link is the only
 * way the JWT can cross from the dashboard origin to the customer's, so it
 * arrives once — as `?hbReviewPage=1#hbReviewToken=<jwt>` (fragment, see
 * REVIEW_TOKEN_FRAG) or in the legacy form `?hbReviewPage=1&t=<jwt>`. On that
 * first load we park it in
 * sessionStorage (per tab + origin, survives same-origin full-page
 * navigation and F5, gone with the tab) and strip it from the address bar at
 * once; cross-page navigation and reloads then carry only `hbReviewPage=1`.
 * Before #197 the JWT stayed in the URL for the whole session and every page
 * hop re-appended it — browser history (incl. sync), Referer and the
 * customer's own analytics saw it. When sessionStorage is blocked the token
 * stays where it came (fallback), and page hops carry it in the fragment.
 * Closing the panel removes it.
 * The device nonce (#87) is unaffected: it lives in localStorage, keyed per
 * token, and is presented on every call as before.
 */
const REVIEW_TOKEN_KEY = 'helpbob_review_token';

/** Parks the token; true only when it reads back (storage really usable). */
function stashReviewToken(token: string): boolean {
  try {
    sessionStorage.setItem(REVIEW_TOKEN_KEY, token);
    return sessionStorage.getItem(REVIEW_TOKEN_KEY) === token;
  } catch {
    return false;
  }
}

function stashedReviewToken(): string | null {
  try { return sessionStorage.getItem(REVIEW_TOKEN_KEY); } catch { return null; }
}

function dropReviewToken(): void {
  try { sessionStorage.removeItem(REVIEW_TOKEN_KEY); } catch { /* blocked — nothing stored */ }
}

/**
 * Since scanner#197 (second step) the deep link carries the JWT in the
 * FRAGMENT, `?hbReviewPage=1&hbFocus=<id>#hbReviewToken=<jwt>`: a fragment is
 * never sent to the customer's server (no access log, not even on the very
 * first request), never in a Referer, and analytics drop it by default — the
 * same reason OAuth's implicit flow uses it. The key is a `key=value` pair in
 * the hb* namespace, so it cannot be mistaken for an element id; should the
 * fragment also hold the page's own anchor or hash route, the parts are
 * `&`-joined and only ours is taken out. The old query form `t=<jwt>` is
 * still read (links minted before the backend switched).
 */
const REVIEW_TOKEN_FRAG = 'hbReviewToken=';

/** Takes the token part out of a fragment. `hash` is what remains ('' or
 *  '#…'), unchanged when the fragment held no token. */
function splitFragmentToken(hash: string): { token: string | null; hash: string } {
  let token: string | null = null;
  let found = false;
  const rest = hash.replace(/^#/, '').split('&').filter((part) => {
    if (!part.startsWith(REVIEW_TOKEN_FRAG)) return true;
    found = true;
    try { token = decodeURIComponent(part.slice(REVIEW_TOKEN_FRAG.length)) || token; } catch { /* malformed — ignore */ }
    return false;
  }).join('&');
  return found ? { token, hash: rest ? '#' + rest : '' } : { token: null, hash };
}

class HelpBobFixerClient {
  // locale stays optional — it's resolved dynamically (config → <html lang> → 'en'),
  // so it must not be forced present like the other defaults.
  private config: Required<Omit<HelpBobConfig, 'locale'>> & { locale?: 'en' | 'de' };
  private applier = new PatchApplier();
  private patches: PublicPatch[] = [];
  /** The page (currentPageUrl) whose patches `patches` holds; null while a
   *  navigation's load is still in flight (scanner#355). */
  private patchesUrl: string | null = null;
  /** Bumped by every load and by destroy(): only the latest load may apply
   *  its answer (scanner#355). */
  private loadSeq = 0;
  private initialized = false;
  private watcher: MutationWatcher | null = null;
  private routeDetector: RouteDetector | null = null;
  private analyticsTracker: PatchAnalytics | null = null;

  constructor(config: HelpBobConfig) {
    this.config = {
      serverUrl: config.serverUrl.replace(/\/$/, ''),
      apiKey: config.apiKey,
      autoApply: config.autoApply ?? true,
      cacheTtl: config.cacheTtl ?? 300,
      watchDom: config.watchDom ?? true,
      watchRoutes: config.watchRoutes ?? true,
      analytics: config.analytics ?? true,
      locale: config.locale,
    };
  }

  /**
   * Initialize: fetch patches and optionally apply them.
   * Starts DOM watcher, route detector, and analytics if enabled.
   */
  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    // Review-on-page deep link — the dashboard opened this tab with a signed
    // token. We DON'T start the steady-state pipeline in this mode; the user
    // is here to evaluate proposed changes via the review UI.
    const params = new URLSearchParams(window.location.search);
    // Page-scope review session — the multi-patch panel. The token comes from
    // the deep link on the first page and from sessionStorage on every later
    // one (scanner#197, see REVIEW_TOKEN_KEY).
    if (params.get('hbReviewPage')) {
      // Fragment form first (current links), then the legacy query form.
      const frag = splitFragmentToken(window.location.hash);
      const queryToken = params.get('t');
      const urlToken = frag.token || queryToken;
      // A fresh deep link always wins over a token parked by an earlier one.
      const tokenInUrl = !!urlToken && !stashReviewToken(urlToken);
      if (urlToken && !tokenInUrl) {
        // Parked — take it out of the address bar before anything else runs
        // on the page (history entry, Referer of later requests, analytics).
        // The page's own anchor, if any, stays in the fragment; the query is
        // only rewritten when it actually held the token.
        const clean = new URL(window.location.href);
        if (queryToken) clean.searchParams.delete('t');
        window.history.replaceState(window.history.state, '', clean.pathname + clean.search + frag.hash);
      }
      const reviewToken = urlToken || stashedReviewToken();
      if (reviewToken) {
        await this.initReviewSessionMode(reviewToken, params.get('hbFocus'), tokenInUrl);
        return;
      }
    }
    // The legacy single-patch deep link (?hbReview=<patchId>) was retired in
    // #199 — nothing has minted one since the panel rollout and the tokens
    // lived 1 h. A stale link now falls through to the normal patch pipeline
    // instead of a review surface; the param itself is stripped below.

    // A start-up redirect (/ → /de/) can move the page while we wait: the
    // load then fetches again for where we are now (scanner#355). null =
    // destroy() ran meanwhile.
    const loaded = await this.loadPatches();
    if (!loaded) return;
    this.patches = loaded.patches;
    this.patchesUrl = loaded.pageUrl;

    // Analytics BEFORE the first applyAll (N4-3): applyAll records 'applied'
    // only into an existing tracker — started afterwards, the first
    // application of every page load was never counted.
    if (this.config.analytics) {
      this.analyticsTracker = new PatchAnalytics(this.config.serverUrl, this.config.apiKey);
      this.analyticsTracker.start();
    }

    if (this.config.autoApply) {
      this.applyAll();
    }

    // Start MutationObserver for dynamic content
    if (this.config.watchDom && this.patches.length > 0) {
      this.watcher = new MutationWatcher(this.patches, this.applier);
      this.watcher.start();
    }

    // Start SPA route detection
    if (this.config.watchRoutes) {
      this.routeDetector = new RouteDetector((newUrl, _oldUrl) => {
        this.onRouteChange(newUrl);
      });
      this.routeDetector.start();
    }
  }

  /** Review-UI locale (config → <html lang> → 'en') + its localized error map. */
  private get wLocale(): WidgetLocale { return resolveWidgetLocale(this.config.locale); }
  private get errS() { return errorStrings(this.wLocale); }

  /**
   * Device-binding nonce store (#87). The engine binds every review token to
   * the first device that redeems it and hands that device a one-time nonce;
   * we must present it (X-HB-Review-Device) on every later review call or get
   * device_mismatch. Keyed PER TOKEN (signature suffix — unique per mint) so
   * two concurrent sessions on one device can't evict each other's binding.
   * localStorage survives the multi-page navigation; the in-memory map keeps
   * the CURRENT page working when storage is blocked (private mode etc.).
   * Not cleared on panel close — re-opening the same link from history on the
   * same device must keep working; expired entries are swept on write.
   */
  private reviewNonceMem = new Map<string, string>();

  private reviewNonceKey(token: string): string {
    return 'helpbob_review_device:' + token.slice(-16);
  }

  private readReviewNonce(token: string): string | null {
    const mem = this.reviewNonceMem.get(token);
    if (mem) return mem;
    try {
      const raw = localStorage.getItem(this.reviewNonceKey(token));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { nonce?: string };
      return typeof parsed.nonce === 'string' ? parsed.nonce : null;
    } catch {
      return null; // storage disabled — mem fallback already checked
    }
  }

  private writeReviewNonce(token: string, nonce: string, expiresAt: number): void {
    this.reviewNonceMem.set(token, nonce);
    try {
      localStorage.setItem(this.reviewNonceKey(token), JSON.stringify({ nonce, exp: expiresAt }));
      // Sweep bindings of expired tokens (we share the customer's storage
      // namespace — leave nothing stale behind).
      const now = Math.floor(Date.now() / 1000);
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith('helpbob_review_device:') || k === this.reviewNonceKey(token)) continue;
        try {
          const v = JSON.parse(localStorage.getItem(k) || '{}') as { exp?: number };
          if (typeof v.exp !== 'number' || v.exp < now) localStorage.removeItem(k);
        } catch {
          localStorage.removeItem(k);
        }
      }
    } catch {
      // localStorage full or disabled — the in-memory copy carries this page;
      // cross-page navigation will surface deviceMismatch with recovery copy.
    }
  }

  /**
   * Headers for review API calls (#87): the JWT travels as a header instead
   * of ?t= so it stays out of engine/nginx access logs (in the page URL it
   * only stands on the deep link's first load — #197 — or when sessionStorage
   * is blocked), plus the device nonce when we hold one.
   */
  private reviewHeaders(token: string, extra?: Record<string, string>): Record<string, string> {
    const h: Record<string, string> = {
      'X-API-Key': this.config.apiKey,
      'X-HB-Review-Token': token,
      ...extra,
    };
    const nonce = this.readReviewNonce(token);
    if (nonce) h['X-HB-Review-Device'] = nonce;
    return h;
  }

  /**
   * Spend the site-scope review token when the reviewer closes the panel
   * (#198) — shrinks the replay window on the bound device from the full ~60
   * minute `exp` down to the actual session length.
   *
   * `keepalive` is what makes this survive the close: the reviewer usually
   * navigates or shuts the tab immediately afterwards, and a plain fetch would
   * be cancelled with the document. Failures are swallowed on purpose — this is
   * hardening, not a step the reviewer can be blocked on or asked to retry.
   */
  private revokeReviewSession(token: string): void {
    try {
      fetch(`${this.config.serverUrl}/api/v1/review-session/revoke`, {
        method: 'POST',
        headers: this.reviewHeaders(token),
        keepalive: true,
      }).catch(() => { /* best-effort */ });
    } catch { /* fetch unavailable / blocked */ }
  }

  /**
   * Review-session mode — invoked when the URL carries ?hbReviewPage=1 and a
   * token is at hand: `#hbReviewToken=<jwt>` (legacy: `t=<jwt>`) on the deep
   * link's first load (site-scope token minted by the dashboard's "Review on
   * page" button), afterwards from sessionStorage (#197). `tokenInUrl` =
   * storage was blocked, so the token still rides in the URL and page hops
   * must re-append it (in the fragment).
   *
   * Fetches EVERY reviewable patch for the current page from the engine,
   * applies the active ones (they're live for visitors anyway), and mounts
   * the multi-patch review panel: per-change preview toggles, accept/reject
   * with reasons, bulk actions and cross-page navigation. Does not start the
   * mutation watcher or route detector — the reviewer is here for a
   * fixed-point snapshot, not the steady-state browsing experience.
   */
  private async initReviewSessionMode(token: string, focusParam: string | null, tokenInUrl: boolean): Promise<void> {
    let panel: { dispose: () => void } | null = null;

    const stripReviewParams = () => {
      const clean = new URL(window.location.href);
      clean.searchParams.delete('hbReviewPage');
      clean.searchParams.delete('hbFocus');
      clean.searchParams.delete('hbReview');
      clean.searchParams.delete('t');
      // Fallback sessions keep the token in the fragment — out with it, the
      // page's own anchor stays.
      window.history.replaceState({}, '', clean.pathname + (clean.search || '') + splitFragmentToken(clean.hash).hash);
    };

    const noop = () => { /* error panel has no actions */ };
    const showError = (msg: string) => {
      panel = mountReviewPanel({
        domain: window.location.hostname,
        pagePath: window.location.pathname,
        changes: [],
        otherPages: [],
        expiresAt: 0,
        locale: this.wLocale,
        error: msg,
        onTogglePreview: noop,
        onDecide: async () => { /* unreachable in error state */ },
        onHighlight: noop,
        onReveal: noop,
        onNavigate: noop,
        onClose: () => {
          panel?.dispose();
          // Dead link (expired, consumed, other device …): leave review mode
          // for good, so a reload doesn't show the same error again (#197).
          dropReviewToken();
          stripReviewParams();
        },
      });
    };

    const friendlyError = (code: string) => (
      code === 'token_expired'
        ? this.errS.linkExpired
        : code === 'patch_not_found'
          ? this.errS.patchGone
          : code === 'token_consumed'
            ? this.errS.tokenConsumed
            : code === 'device_mismatch'
              ? this.errS.deviceMismatch
              : code === 'review_state_unavailable'
                ? this.errS.reviewTempUnavailable
                : this.errS.linkInvalid
    );

    try {
      // Das JWT reist nur im Header (#87). `url` ohne Review-Parameter,
      // Fragment und Tracking (scanner#358) — sonst stuende es doch in den
      // Access-Logs, eingebettet in die URL der Seite. `caps` wie bei
      // GET /patches: ohne sie haelt der Server compound zurueck (#354).
      const url = `${this.config.serverUrl}/api/v1/review-session`
        + `?url=${encodeURIComponent(reportPageUrl(window.location.href))}&caps=${CLIENT_CAPS}`;
      const fetchSession = () => fetch(url, { headers: this.reviewHeaders(token) });
      let res = await fetchSession();
      if (!res.ok) {
        let body = await res.json().catch(() => ({} as { error?: string }));
        // Same-device two-tab race: browser session restore can fire tab B's
        // request before tab A's nonce write lands in localStorage. Re-read
        // and retry exactly once before showing the mismatch error.
        if ((body as { error?: string }).error === 'device_mismatch') {
          await new Promise((r) => setTimeout(r, 250));
          res = await fetchSession();
          if (!res.ok) body = await res.json().catch(() => ({} as { error?: string }));
        }
        if (!res.ok) {
          showError(friendlyError((body as { error?: string }).error || `http_${res.status}`));
          return;
        }
      }
      const data = await res.json() as {
        siteId: string;
        pageUrl: string;
        patches: Array<PublicPatch & {
          pageUrl: string; why: string; violationHtml: string | null; fixCode: string | null;
        }>;
        otherPages: Array<{ path: string; pendingCount: number }>;
        expiresAt: number;
        focusPatchId: string | null;
        deviceNonce?: string;
      };
      // First redemption bound this device (#87) — persist the nonce; every
      // later call (decides, cross-page navigations, F5) must present it.
      if (data.deviceNonce) this.writeReviewNonce(token, data.deviceNonce, data.expiresAt);

      const byId = new Map(data.patches.map((p) => [p.id, p]));
      const focusPatchId = (focusParam && byId.has(focusParam) ? focusParam : null)
        || (data.focusPatchId && byId.has(data.focusPatchId) ? data.focusPatchId : null);

      // Active patches are what visitors currently see — apply them so the
      // reviewer starts from the live state. Reverse document order, same
      // reason as applyAll (see #83).
      const active = data.patches.filter((p) => p.status === 'active');
      this.applier.pinTargets(active);
      for (const patch of this.sortByReverseDocumentOrder(active)) {
        this.applier.apply(patch);
      }

      // Telemetry — review_opened once per session (anchored on the focus
      // patch so funnel queries still join on a real patch id). Analytics
      // opt-out (`analytics: false`) is respected.
      let analytics: PatchAnalytics | null = null;
      const anchor = (focusPatchId && byId.get(focusPatchId)) || data.patches[0];
      if (this.config.analytics && anchor) {
        analytics = new PatchAnalytics(this.config.serverUrl, this.config.apiKey);
        analytics.start();
        analytics.record(anchor.id, 'review_opened', anchor.selector);
      }

      // Resolve a patch selector to a *visible* element to frame. Hidden inputs
      // (and other 0×0 / display:none / visibility:hidden nodes) can't show an
      // outline themselves, so we climb to the nearest visible ancestor and
      // flag it as a fallback — the caller draws a dashed "area" frame instead.
      // Defined before `changes` so each card can carry a `hiddenArea` marker.
      const isVisible = (n: HTMLElement): boolean => {
        const r = n.getBoundingClientRect();
        return r.width > 0 && r.height > 0 && getComputedStyle(n).visibility !== 'hidden';
      };
      // When the element is hidden AND has no visible ancestor to bracket (e.g.
      // it lives in a collapsed mobile menu / closed modal), we can't frame it.
      // Name the region it sits in instead, so the card still says WHERE it is:
      // the outermost identifiable ancestor (id / aria-label / role / landmark).
      const LANDMARK_TAGS = new Set(['NAV', 'DIALOG', 'ASIDE', 'HEADER', 'FOOTER', 'FORM', 'MAIN', 'SECTION']);
      const nodeIdentifier = (node: Element | null): string | null => {
        if (!node || node.nodeType !== 1) return null;
        if (node.id) return '#' + node.id;
        const label = node.getAttribute('aria-label');
        if (label && label.trim()) return node.tagName.toLowerCase() + ' „' + label.trim() + '"';
        const role = node.getAttribute('role');
        if (role && role.trim()) return node.tagName.toLowerCase() + ' (role=' + role.trim() + ')';
        if (LANDMARK_TAGS.has(node.tagName)) return node.tagName.toLowerCase();
        return null;
      };
      const describeHiddenContainer = (el: HTMLElement): string | null => {
        let best: string | null = null;
        let p: Element | null = el;
        while (p && p !== document.body && p !== document.documentElement) {
          const id = nodeIdentifier(p);
          if (id) best = id;
          p = p.parentElement;
        }
        return best;
      };
      const resolveTarget = (
        selector: string,
      ): { el: HTMLElement; fallback: boolean; hiddenNoArea?: boolean; hiddenContainer?: string | null } | null => {
        const el = querySelectorAllDeep(document, selector)[0] as HTMLElement | undefined;
        if (!el) return null;
        if (isVisible(el)) return { el, fallback: false };
        let p: HTMLElement | null = el.parentElement;
        while (p && p !== document.body) {
          if (isVisible(p)) return { el: p, fallback: true };
          p = p.parentElement;
        }
        return { el, fallback: true, hiddenNoArea: true, hiddenContainer: describeHiddenContainer(el) };
      };

      const changes: ReviewChange[] = data.patches.map((p) => {
        const codeParts: string[] = [];
        if (p.violationHtml) codeParts.push(p.violationHtml);
        codeParts.push(describeAction(p.action, p.selector));
        if (p.fixCode) codeParts.push(`Suggested source fix:\n${p.fixCode}`);
        // Hidden target → the card shows an "approximate area" marker, matching
        // the dashed area frame applyOutline draws when this card is framed.
        const resolved = resolveTarget(p.selector);
        return {
          id: p.id,
          state: (p.status === 'pending_review' ? 'pending' : p.status) as ReviewChangeState,
          rule: p.ruleId,
          wcag: p.wcagSC && p.wcagSC.length > 0 ? p.wcagSC[0] : '',
          summary: p.description || 'No description provided.',
          why: p.why,
          code: codeParts.join('\n\n'),
          ai: p.createdBy === 'llm',
          hiddenArea: !!(resolved && resolved.fallback && !resolved.hiddenNoArea),
          hiddenNoArea: !!(resolved && resolved.hiddenNoArea),
          hiddenContainer: (resolved && resolved.hiddenContainer) || undefined,
        };
      });

      // Frame an element with an outline, snapshotting its inline styles so we
      // restore exactly what the customer page had. Visible elements get a
      // solid frame; fallback "area" frames are dashed AND carry a small label
      // pinned to the frame, making explicit that the real element is hidden and
      // this is only its approximate area (a dashed-vs-solid line alone is too
      // subtle to notice). The label is document-positioned so it tracks the
      // frame on scroll, and is removed again on restore().
      const applyOutline = (el: HTMLElement, fallback: boolean): { restore: () => void } => {
        const outline = el.style.outline;
        const offset = el.style.outlineOffset;
        el.style.outline = `3px ${fallback ? 'dashed' : 'solid'} #fcc419`;
        el.style.outlineOffset = '3px';

        let label: HTMLElement | null = null;
        if (fallback) {
          const r = el.getBoundingClientRect();
          label = document.createElement('div');
          label.setAttribute('data-helpbob-area-label', '');
          label.textContent = `≈ ${panelStrings(this.wLocale).approxArea}`;
          label.style.cssText = [
            'position:absolute',
            `top:${Math.round(r.top + window.scrollY + 2)}px`,
            `left:${Math.round(r.left + window.scrollX + 2)}px`,
            'max-width:260px', 'box-sizing:border-box',
            'background:#1a1714', 'color:#fff',
            'font:700 11px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
            'padding:2px 7px', 'border-radius:6px',
            'box-shadow:0 1px 4px rgba(0,0,0,.4)',
            'z-index:2147483647', 'pointer-events:none',
          ].join(';');
          document.body.appendChild(label);
        }

        return {
          restore: () => {
            el.style.outline = outline;
            el.style.outlineOffset = offset;
            if (label && label.parentNode) label.parentNode.removeChild(label);
          },
        };
      };

      // Persistent hover outline — one snapshot per card id.
      const outlined = new Map<string, { restore: () => void }>();
      const setHighlight = (id: string, on: boolean) => {
        if (!on) {
          const o = outlined.get(id);
          if (o) { o.restore(); outlined.delete(id); }
          return;
        }
        if (outlined.has(id)) return;
        const p = byId.get(id);
        const t = p ? resolveTarget(p.selector) : null;
        if (!t || t.hiddenNoArea) return; // nothing visible to frame
        outlined.set(id, applyOutline(t.el, t.fallback));
      };

      // Click-to-reveal frame — exactly one at a time, persists until the next
      // reveal (or the panel closes), so the reviewer can scroll around while
      // the framed element stays marked.
      let revealed: { restore: () => void } | null = null;
      const revealCard = (id: string) => {
        const p = byId.get(id);
        const t = p ? resolveTarget(p.selector) : null;
        if (!t || t.hiddenNoArea) return; // hidden in a collapsed region — nothing to scroll to / frame
        revealed?.restore();
        t.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        revealed = applyOutline(t.el, t.fallback);
      };

      panel = mountReviewPanel({
        domain: window.location.hostname,
        pagePath: window.location.pathname,
        changes,
        otherPages: data.otherPages,
        focusPatchId,
        expiresAt: data.expiresAt,
        locale: this.wLocale,
        onTogglePreview: (id, applied) => {
          const p = byId.get(id);
          if (!p) return;
          if (applied) {
            const n = this.applier.apply(p, { preview: true });
            if (n > 0) {
              (document.querySelector(p.selector) as HTMLElement | null)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          } else {
            try { this.applier.rollback(id); } catch { /* DOM gone */ }
          }
        },
        onDecide: async (id, decision, reason) => {
          // POST the decision to the engine. The site-scope token in the URL
          // authenticates the call; the engine verifies the patch belongs to
          // the token's site.
          const decideUrl = `${this.config.serverUrl}/api/v1/review/${encodeURIComponent(id)}/decide`;
          const res = await fetch(decideUrl, {
            method: 'POST',
            headers: this.reviewHeaders(token, { 'Content-Type': 'application/json' }),
            body: JSON.stringify({ decision, reason }),
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({} as { error?: string }));
            const code = (body as { error?: string }).error || `http_${res.status}`;
            throw new Error(code === 'token_expired'
              ? this.errS.linkExpired
              : code === 'patch_not_found'
                ? this.errS.patchGoneShort
                : code === 'token_consumed'
                  ? this.errS.tokenConsumed
                  : code === 'device_mismatch'
                    ? this.errS.deviceMismatch
                    : code === 'review_state_unavailable'
                      ? this.errS.reviewTempUnavailable
                      : this.errS.saveError);
          }
          // Decision persisted — sync the page and our status snapshot so a
          // later Close rolls back exactly the still-undecided previews.
          const p = byId.get(id);
          if (decision === 'approve') {
            if (p) {
              p.status = 'active';
              this.applier.apply(p, { preview: true }); // no-op if already applied
            }
          } else if (decision === 'reject' || decision === 'disable') {
            if (p) p.status = decision === 'reject' ? 'rejected' : 'disabled';
            try { this.applier.rollback(id); } catch { /* DOM gone */ }
          } else if (p) {
            p.status = 'pending_review';
          }
          analytics?.record(
            id,
            decision === 'approve' ? 'review_approved'
              : decision === 'reject' ? 'review_rejected'
              : decision === 'reinstate' ? 'review_reinstated'
              : 'review_disabled',
            p?.selector || '',
          );
          // Flush immediately rather than wait for the 30s interval — the
          // user often closes the tab seconds after deciding.
          analytics?.flush().catch(() => { /* analytics is best-effort */ });
        },
        onHighlight: setHighlight,
        onReveal: revealCard,
        onNavigate: (path) => {
          // Same token — it's site-scoped, so it stays valid on the next page.
          // It waits in sessionStorage (#197); only when storage is blocked
          // does it have to ride along — then in the fragment, never in the
          // query, whichever form the link came in (the server of the next
          // page must not see it either).
          const at = path.indexOf('#');
          const base = at < 0 ? path : path.slice(0, at);
          const anchor = at < 0 ? '' : path.slice(at + 1);
          const hash = [anchor, tokenInUrl ? REVIEW_TOKEN_FRAG + encodeURIComponent(token) : '']
            .filter(Boolean).join('&');
          const dest = `${base}${base.includes('?') ? '&' : '?'}hbReviewPage=1${hash ? '#' + hash : ''}`;
          // Sicherheitspruefung 13.10.2026 (S3 N3): der Pfad kommt aus der
          // gespeicherten Seitenadresse eines Patches. `//evil.example/x`
          // (Pfad einer Seite `https://kunde.example//evil.example/x`) waere
          // eine protokollrelative Adresse — der Browser ginge zu
          // evil.example, bei gesperrtem Storage mit dem Token im Fragment.
          // Nur dieselbe Origin wird angesteuert.
          let target: URL;
          try { target = new URL(dest, window.location.href); } catch { return; }
          if (target.origin !== window.location.origin) {
            console.warn('[HelpBob] Review panel: refusing cross-origin navigation');
            return;
          }
          this.navigateTo(dest);
        },
        onClose: () => {
          // Roll back undecided previews so the reviewer sees the page as
          // visitors will; approved/active patches stay applied — they're
          // the live state anyway. Then strip the review params so a refresh
          // doesn't re-enter review mode.
          for (const p of data.patches) {
            if (p.status !== 'active') {
              try { this.applier.rollback(p.id); } catch { /* DOM gone */ }
            }
          }
          for (const id of Array.from(outlined.keys())) setHighlight(id, false);
          revealed?.restore();
          revealed = null;
          if (anchor) analytics?.record(anchor.id, 'review_closed', anchor.selector);
          analytics?.stop();
          // End the server-side session too (#198). Without this the token
          // stays valid on THIS device until `exp` (~60 min) even though the
          // review is over — the panel closing is the only moment we know the
          // session is actually finished. Best-effort and deliberately not
          // awaited: closing the panel must never block on the network, and
          // the server treats a repeated revoke as a no-op.
          this.revokeReviewSession(token);
          dropReviewToken();
          stripReviewParams();
        },
      });
    } catch (err) {
      console.warn('[HelpBob] review-session fetch failed:', err);
      showError(this.errS.serverUnreachable);
    }
  }

  /** Full-page navigation of the review panel (own method so tests can observe it). */
  private navigateTo(url: string): void {
    window.location.href = url;
  }

  /**
   * Apply all active patches.
   *
   * Patches are applied in reverse document order so that mutations below
   * don't shift `:nth-of-type` indices used by patches above. Without this,
   * two sibling `replaceTag` patches against `p.fake-heading:nth-of-type(1)`
   * and `p.fake-heading:nth-of-type(3)` would leave the second unfixed
   * because the first mutation removes a `<p>` from the index sequence.
   * See #83.
   */
  applyAll(): number {
    const ordered = this.sortByReverseDocumentOrder(this.patches);
    this.applier.pinTargets(ordered);
    let count = 0;
    for (const patch of ordered) {
      const applied = this.applier.apply(patch);
      count += applied;
      if (applied > 0 && this.analyticsTracker) {
        this.analyticsTracker.record(patch.id, 'applied', patch.selector);
      }
    }
    return count;
  }

  /**
   * Resolve each patch's first matching element and sort so that the
   * latest-in-document targets are mutated first. Patches with no match
   * fall to the end (still attempted; analytics will mark them as misses).
   */
  private sortByReverseDocumentOrder<P extends { selector: string }>(patches: P[]): P[] {
    return sortPatchesByReverseDocumentOrder(patches);
  }

  /**
   * Remove all patches, restoring original DOM.
   */
  removeAll(): void {
    this.applier.rollbackAll();
  }

  /**
   * Apply a specific patch by ID.
   */
  applyPatch(patchId: string): number {
    const patch = this.patches.find(p => p.id === patchId);
    if (!patch) return 0;
    return this.applier.apply(patch);
  }

  /**
   * Remove a specific patch by ID.
   */
  removePatch(patchId: string): boolean {
    return this.applier.rollback(patchId);
  }

  /**
   * Get all loaded patches.
   */
  getPatches(): PublicPatch[] {
    return [...this.patches];
  }

  /**
   * Get IDs of currently applied patches.
   */
  getAppliedPatchIds(): string[] {
    return this.applier.getAppliedIds();
  }

  /**
   * MutationWatcher processing/budget counters (F7) — null when the
   * watcher isn't running (watchDom: false, review mode, no patches).
   */
  getMutationStats(): MutationWatcherStats | null {
    return this.watcher ? this.watcher.getPerfStats() : null;
  }

  /**
   * Fully shut down the client: stop watchers, analytics, remove patches.
   */
  destroy(): void {
    // A load still in flight must not apply its answer after this (scanner#355).
    this.loadSeq++;
    this.patchesUrl = null;
    this.watcher?.stop();
    this.routeDetector?.stop();
    this.analyticsTracker?.stop();
    this.applier.rollbackAll();
    this.watcher = null;
    this.routeDetector = null;
    this.analyticsTracker = null;
    this.initialized = false;
  }

  /**
   * Handle SPA route change: rollback current patches, re-fetch for new URL.
   */
  private async onRouteChange(_newUrl: string): Promise<void> {
    // Same page, other fragment or tracking parameter — an anchor, a skip link
    // (`#main`), a `?utm_…` rewrite: nothing to swap (scanner#355). Before,
    // this rolled everything back and asked the server for `…#main`, which it
    // never scanned, so every patch vanished.
    if (currentPageUrl() === this.patchesUrl) return;

    // Park the watcher on an empty patch list FIRST: fetching is async, and a
    // debounce timer or deferred slice firing during the await would otherwise
    // apply the OLD page's patches to the new DOM (after rollbackAll already
    // ran), leaving effects nothing re-applies or reverts. Nodes queued during
    // the transition are covered by the applyAll below, which sweeps the whole
    // document.
    this.watcher?.updatePatches([]);

    // Invalidate cache for new URL
    this.clearCache();

    // Rollback current patches
    this.applier.rollbackAll();
    this.patches = [];
    this.patchesUrl = null;

    // Fetch patches for new URL. null = a later navigation (or destroy)
    // superseded this one: its load applies, this answer is dropped — on a
    // quick A→B→C, B's late answer must never land on C (scanner#355).
    const loaded = await this.loadPatches();
    if (!loaded) return;
    this.patches = loaded.patches;
    this.patchesUrl = loaded.pageUrl;

    if (this.config.autoApply) {
      this.applyAll();
    }

    // Update watcher with new patches
    if (this.watcher) {
      this.watcher.updatePatches(this.patches);
    }
  }

  /**
   * Load the patches of the page we are on NOW (scanner#355). The page can
   * move while the request is out — a start-up redirect (/ → /de/), a quick
   * navigation — and an answer only counts while its page is still the
   * current one: otherwise it is fetched again for where we are
   * (MAX_LOAD_ATTEMPTS). Returns null when a later load (or destroy) took
   * over; that one applies, this one must not touch anything.
   * `pageUrl: null` = the page never held still — nothing to apply.
   */
  private async loadPatches(): Promise<{ pageUrl: string | null; patches: PublicPatch[] } | null> {
    const seq = ++this.loadSeq;
    for (let attempt = 0; attempt < MAX_LOAD_ATTEMPTS; attempt++) {
      const pageUrl = currentPageUrl();
      const patches = await this.fetchPatches(pageUrl);
      if (seq !== this.loadSeq) return null;
      if (currentPageUrl() === pageUrl) return { pageUrl, patches };
    }
    return { pageUrl: null, patches: [] };
  }

  /**
   * Fetch patches from server, with localStorage cache fallback.
   * `pageUrl` (currentPageUrl) is the request's `url` AND the cache key
   * (#352): the page can change its URL while we await, and the entry must be
   * filed under the url the server actually answered for.
   */
  private async fetchPatches(pageUrl: string): Promise<PublicPatch[]> {
    // Try cache first
    const cached = this.readCache(pageUrl);
    if (cached) return cached.patches;

    try {
      const url = `${this.config.serverUrl}/api/v1/patches?url=${encodeURIComponent(pageUrl)}&caps=${CLIENT_CAPS}`;
      const res = await fetch(url, {
        headers: { 'X-API-Key': this.config.apiKey },
      });

      if (!res.ok) {
        console.warn(`[HelpBob] Failed to fetch patches: ${res.status}`);
        return [];
      }

      const data: FixerResponse = await res.json();
      this.writeCache(pageUrl, data);
      return data.patches;
    } catch (err) {
      console.warn('[HelpBob] Network error fetching patches:', err);
      return [];
    }
  }

  /** Cache key of one page — the exact string fetchPatches sends as `url` (#352, #355). */
  private patchCacheKey(pageUrl: string): string {
    return CACHE_PREFIX + pageUrl;
  }

  private readCache(pageUrl: string): FixerResponse | null {
    try {
      const raw = localStorage.getItem(this.patchCacheKey(pageUrl));
      if (!raw) return null;

      const { data, timestamp } = JSON.parse(raw);
      const age = (Date.now() - timestamp) / 1000;
      if (age > this.config.cacheTtl) return null;

      return data as FixerResponse;
    } catch {
      return null;
    }
  }

  private writeCache(pageUrl: string, data: FixerResponse): void {
    const key = this.patchCacheKey(pageUrl);
    const now = Date.now();
    // Prune first: frees room when the quota is tight
    this.pruneCache(key, now);
    try {
      localStorage.setItem(key, JSON.stringify({
        data,
        timestamp: now,
      }));
    } catch {
      // localStorage might be full or disabled - that's fine
    }
  }

  /**
   * Keep the per-page cache bounded (#352) — every page visited within
   * cacheTtl leaves an entry in the customer's storage namespace. Drops
   * unparsable and expired entries, then all but the newest
   * CACHE_MAX_ENTRIES - 1 others, so with the entry written next there are at
   * most CACHE_MAX_ENTRIES. The legacy key goes too, and so does every entry
   * an older bundle filed under the raw href — its key can hold a fragment
   * (`#access_token=…`) or tracking parameters (scanner#355). Keys are
   * collected before anything is removed (removal shifts localStorage.key
   * indices).
   */
  private pruneCache(currentKey: string, now: number): void {
    try {
      localStorage.removeItem(LEGACY_CACHE_KEY);
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(CACHE_PREFIX) && k !== currentKey) keys.push(k);
      }
      const live: Array<{ key: string; timestamp: number }> = [];
      for (const key of keys) {
        let timestamp: unknown;
        try {
          timestamp = (JSON.parse(localStorage.getItem(key) || '') as { timestamp?: unknown }).timestamp;
        } catch {
          // Unparsable — removed below
        }
        const pageUrl = key.slice(CACHE_PREFIX.length);
        if (typeof timestamp === 'number' && (now - timestamp) / 1000 <= this.config.cacheTtl
          && normalizePageUrl(pageUrl) === pageUrl) {
          live.push({ key, timestamp });
        } else {
          localStorage.removeItem(key);
        }
      }
      live.sort((a, b) => b.timestamp - a.timestamp);
      for (const { key } of live.slice(CACHE_MAX_ENTRIES - 1)) localStorage.removeItem(key);
    } catch {
      // localStorage disabled - nothing to prune
    }
  }

  /** Drops the current page's entry — on a route change that is the NEW route. */
  private clearCache(): void {
    try {
      localStorage.removeItem(this.patchCacheKey(currentPageUrl()));
    } catch {
      // Ignore
    }
  }
}

/**
 * The first element a selector matches, or null — also when this browser
 * REJECTS the selector (scanner#367). `querySelector` throws a SyntaxError on
 * a selector it cannot parse (a pseudo-class it does not know, a stored
 * selector with broken escaping). Here that throw escaped the sort, so
 * applyAll never ran and ONE bad patch took every patch of the page with it.
 * A rejected selector sorts like a miss; PatchApplier.apply already treats it
 * as 0 matches (querySelectorAllDeep), so only that patch drops out.
 */
function firstMatchOrNull(selector: string): Element | null {
  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
}

/**
 * Sort patches so the latest-in-document targets are mutated first —
 * mutations below must not shift `:nth-of-type` indices used by patches
 * above (#83). Shared by the visitor apply path (applyAll) and the
 * server-side verification entry below.
 */
function sortPatchesByReverseDocumentOrder<P extends { selector: string }>(patches: P[]): P[] {
  const resolved = patches.map(patch => ({
    patch,
    firstMatch: firstMatchOrNull(patch.selector),
  }));
  resolved.sort((a, b) => {
    if (!a.firstMatch && !b.firstMatch) return 0;
    if (!a.firstMatch) return 1;
    if (!b.firstMatch) return -1;
    const rel = a.firstMatch.compareDocumentPosition(b.firstMatch);
    if (rel & Node.DOCUMENT_POSITION_FOLLOWING) return 1;
    if (rel & Node.DOCUMENT_POSITION_PRECEDING) return -1;
    return 0;
  });
  return resolved.map(r => r.patch);
}

/**
 * Server-side verification entry point (#115).
 *
 * Applies a batch of patches to the CURRENT document exactly like the
 * visitor path — same PatchApplier (all guards: expectedImg identity,
 * alt-clobber, never-overwrite ARIA, sanitization, shadow DOM), same
 * reverse-document-order (#83) — but with none of the client runtime:
 * no fetch, no cache, no analytics, no watchers, no review UI.
 *
 * `preview: true` bypasses the status==='active' gate so the browser
 * verifier can also judge pending patches; the server decides what it
 * feeds in. Returns per patch how many elements MATCHED — 0 means the
 * selector missed or a pre-apply guard (expectedImg identity, alt-clobber)
 * skipped every element, which the server maps to a 'not_applied' verdict
 * (visitor-faithful, NOT a failure). NOTE: per-action guards that run
 * INSIDE applyAction (existing aria-label/role, class already present)
 * still count their element — exactly like the visitor path's applyAll.
 */
export function applyPatchesForVerification(
  patches: PublicPatch[],
): Array<{ id: string; applied: number }> {
  const applier = new PatchApplier();
  applier.pinTargets(patches);
  return sortPatchesByReverseDocumentOrder(patches).map((patch) => ({
    id: patch.id,
    applied: applier.apply(patch, { preview: true }),
  }));
}

// Auto-init if config is on window
declare global {
  interface Window {
    HelpBobConfig?: HelpBobConfig;
    HelpBob?: HelpBobFixerClient;
  }
}

if (typeof window !== 'undefined' && window.HelpBobConfig) {
  const client = new HelpBobFixerClient(window.HelpBobConfig);
  window.HelpBob = client;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => client.init());
  } else {
    client.init();
  }
}

export { HelpBobFixerClient, PatchApplier, MutationWatcher, RouteDetector, PatchAnalytics };
export type { AccessibilityPatch, FixerResponse, PublicPatch } from '@helpbob/shared';
